import { Editor as TipTapEditor, EditorContent } from '@tiptap/vue-3';
import debounce from 'debounce';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';
import Link from '@tiptap/extension-link';
import Strike from '@tiptap/extension-strike';
import Underline from '@tiptap/extension-underline';
import Superscript from '@tiptap/extension-superscript';
import Text from '@tiptap/extension-text';
import { columnResizingPluginKey } from 'prosemirror-tables';
import { ProsemirrorType, ListItemNode, BulletListNode, HeadingNode, OrderedListNode, ParagraphNode, TableCellNode, TableNode, TableHeaderNode, TableRowNode, TableDocNode, MergeValueNode, HardBreakNode, TextAlign, EditorHelper, } from '@getaccept/editor-lib-new';
import { Colors } from '@getaccept/lib-shared-new/src/types/colors';
import { onBeforeUnmount, onMounted, computed, defineComponent, ref, watch } from 'vue';
import { updateMergeValues, setContent, handleInit } from './helpers/prosemirror-viewer.helper';
export default defineComponent({
    name: 'ProsemirrorViewerTable',
    components: {
        EditorContent,
    },
    props: {
        borderColor: { type: String, default: '' },
        alternateRowColor: { type: String, default: '' },
        headerRowBackgroundColor: { type: String, default: '' },
        isPdfViewer: { type: Boolean, default: false },
        content: {
            type: [Object, String],
            default: '',
        },
        mergeValues: { type: Object, required: true },
        fields: { type: Array, default: () => [] },
    },
    setup(props) {
        const viewer = ref(null);
        const overflow = ref(false);
        const tableWrapper = ref(null);
        const editorContainer = ref(null);
        const setOverflow = () => {
            overflow.value = EditorHelper.hasHiddenContentRight(tableWrapper.value);
        };
        const debounceHandleScroll = debounce(setOverflow, 300);
        const handleMouseDown = ({ state }) => columnResizingPluginKey.getState(state).activeHandle !== -1;
        const handleLoaded = () => {
            var _a, _b;
            handleInit(viewer.value, props.fields, props.mergeValues);
            tableWrapper.value = (_a = editorContainer.value) === null || _a === void 0 ? void 0 : _a.querySelector('.tableWrapper');
            if (!tableWrapper.value) {
                return;
            }
            (_b = tableWrapper.value) === null || _b === void 0 ? void 0 : _b.addEventListener('scroll', debounceHandleScroll);
            setOverflow();
        };
        const contentPayload = computed(() => ({
            viewer: viewer.value,
            content: props.content,
            fields: props.fields,
            mergeValues: props.mergeValues,
        }));
        const tableViewer = computed(() => ({
            content: props.content,
            editable: false,
            enableInputRules: false,
            enablePasteRules: false,
            extensions: [
                TableDocNode,
                ParagraphNode,
                Text,
                Bold,
                Italic,
                Strike,
                Superscript,
                Underline,
                OrderedListNode,
                BulletListNode,
                ListItemNode,
                MergeValueNode,
                Link.configure({
                    autolink: false,
                    openOnClick: true,
                    linkOnPaste: false,
                }),
                TableNode.configure({ resizable: !props.isPdfViewer }),
                TableHeaderNode,
                TableCellNode,
                TableRowNode,
                HeadingNode,
                HardBreakNode,
                TextAlign.configure({
                    types: [
                        ProsemirrorType.Paragraph,
                        ProsemirrorType.Heading,
                        ProsemirrorType.BulletList,
                        ProsemirrorType.OrderedList,
                    ],
                }),
            ],
            onCreate: handleLoaded,
            editorProps: {
                handleDOMEvents: {
                    mousedown: handleMouseDown,
                },
            },
        }));
        onMounted(() => {
            viewer.value = new TipTapEditor(tableViewer.value);
        });
        onBeforeUnmount(() => {
            var _a;
            (_a = tableWrapper.value) === null || _a === void 0 ? void 0 : _a.removeEventListener('scroll', debounceHandleScroll);
            viewer.value.destroy();
        });
        watch(() => [props.fields, props.mergeValues], () => {
            updateMergeValues(contentPayload.value);
        });
        watch(() => props.content, () => {
            setContent(contentPayload.value);
        });
        return {
            viewer,
            Colors,
            editorContainer,
            handleMouseDown,
            setOverflow,
            handleLoaded,
            overflow,
            tableWrapper,
        };
    },
});
